// Presentational replica of the in-app Reddit Gaps interface, rendered with
// demo data for marketing pages. No server calls, no account state.

import { ArrowUp, CheckCircle2, ExternalLink, MessageSquare, Sparkles } from "lucide-react";

const DEMO_DOMAIN = "acmeflow.com";

type DemoThread = {
  id: string;
  title: string;
  subreddit: string;
  upvotes: number;
  comments: number;
  ageDays: number;
  competitors: string[];
  top?: boolean;
};

const DEMO_THREADS: DemoThread[] = [
  {
    id: "t1",
    title: "What are you all using for cross-team project tracking in 2026?",
    subreddit: "ProductManagement",
    upvotes: 412,
    comments: 168,
    ageDays: 12,
    competitors: ["Asana", "Linear", "ClickUp"],
    top: true,
  },
  {
    id: "t2",
    title: "Monday.com vs Asana for a 60-person ops team, worth the price jump?",
    subreddit: "sysadmin",
    upvotes: 237,
    comments: 94,
    ageDays: 26,
    competitors: ["Monday.com", "Asana"],
  },
  {
    id: "t3",
    title: "Anyone actually happy with their PM tool after 12 months?",
    subreddit: "smallbusiness",
    upvotes: 158,
    comments: 61,
    ageDays: 41,
    competitors: ["ClickUp", "Notion"],
  },
];

const DEMO_STATS: { label: string; value: string; delta?: number; hint?: string; accent?: string }[] = [
  { label: "Threads Found", value: "14", delta: 3 },
  { label: "Upvotes", value: "2.1K", delta: 480 },
  { label: "Comments", value: "623", delta: 97 },
  { label: "Gaps Closed", value: "2", hint: "Resolved in the last 30 days", accent: "var(--signal-teal)" },
  { label: "Potential Visibility", value: "High", hint: "Top opportunity", accent: "var(--signal-teal)" },
];

function DemoBadge() {
  return (
    <span className="rounded-full border border-border bg-panel px-2 py-0.5 text-[13px] text-muted-foreground font-medium">
      Demo data
    </span>
  );
}

function Stat({ label, value, delta, hint, accent }: { label: string; value: string; delta?: number; hint?: string; accent?: string }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className="text-[13px] text-muted-foreground font-medium">{label}</div>
      <div
        className="mt-1.5 font-mono text-2xl font-semibold tracking-tight text-ink"
        style={accent ? { color: accent } : undefined}
      >
        {value}
      </div>
      {typeof delta === "number" ? (
        <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium" style={{ color: "var(--signal-teal)" }}>
          <ArrowUp className="h-3 w-3" />+{delta} since last scan
        </div>
      ) : (
        <div className="mt-1 text-[11px] text-muted-foreground">{hint}</div>
      )}
    </div>
  );
}

function ThreadRow({ thread }: { thread: DemoThread }) {
  return (
    <div className="flex flex-col gap-3 border-b border-border px-4 py-4 last:border-0 lg:flex-row lg:items-start">
      <div className="flex w-14 shrink-0 flex-col items-center rounded-xl bg-panel py-2">
        <ArrowUp className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="font-mono text-sm font-semibold text-ink">{thread.upvotes}</span>
      </div>

      <div className="min-w-0 flex-1">
        <span className="inline-flex items-start gap-1.5 text-sm font-semibold text-ink">
          <span className="line-clamp-2">{thread.title}</span>
          <ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0 opacity-60" />
        </span>
        <div className="mt-1 text-[12px] text-muted-foreground">
          r/{thread.subreddit} • Posted {thread.ageDays} days ago
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {thread.top && (
            <span className="rounded-full bg-brand-accent/10 px-2 py-0.5 text-[11px] font-semibold text-brand-accent-ink">
              Top thread
            </span>
          )}
          <span className="inline-flex items-center gap-1 text-[12px] text-muted-foreground">
            <MessageSquare className="h-3 w-3" />
            {thread.comments} comments
          </span>
          <span className="rounded-full border border-border bg-panel px-2 py-0.5 text-[11px] text-muted-foreground">
            r/{thread.subreddit} rules
          </span>
          <span className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
            <span className="inline-block h-3 w-3 rounded-[4px] border border-border" />
            I commented
          </span>
        </div>
      </div>

      <div className="lg:w-56">
        <div className="text-[11px] text-muted-foreground">Mentions competitors:</div>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {thread.competitors.map((c) => (
            <span key={c} className="rounded-md border border-border bg-panel px-1.5 py-0.5 text-[11px] font-medium text-ink">
              {c}
            </span>
          ))}
        </div>
      </div>

      <div className="lg:w-44">
        <div className="rounded-lg bg-signal-coral/10 px-2.5 py-1.5 text-[11px] font-medium leading-snug text-signal-coral-ink">
          No mention of
          <br />
          {DEMO_DOMAIN}
        </div>
      </div>
    </div>
  );
}

/** Stat strip + open-gap thread list, as it appears in the product. */
export function RedditGapsDemoBoard({ id }: { id?: string }) {
  return (
    <div id={id} className="force-light rounded-2xl border border-border bg-paper p-4 sm:p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="font-display text-lg font-semibold text-ink">Reddit Gaps</div>
          <p className="text-[12px] text-muted-foreground">
            Threads recommending competitors but not mentioning <strong className="text-ink">{DEMO_DOMAIN}</strong>.
          </p>
        </div>
        <DemoBadge />
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {DEMO_STATS.map((s) => (
          <Stat key={s.label} {...s} />
        ))}
      </div>

      <div className="mt-4 rounded-2xl border border-border bg-card">
        {DEMO_THREADS.map((t) => (
          <ThreadRow key={t.id} thread={t} />
        ))}
      </div>
    </div>
  );
}

/** Tracking panel: threads you checked off, and what happened next. */
export function RedditGapsDemoTracking({ id }: { id?: string }) {
  return (
    <div id={id} className="force-light rounded-2xl border border-border bg-paper p-4 sm:p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="font-display text-lg font-semibold text-ink">Commented threads</div>
          <p className="text-[12px] text-muted-foreground">Watching for AI answers that cite these threads.</p>
        </div>
        <DemoBadge />
      </div>

      <div className="rounded-2xl border border-border bg-card">
        <div className="flex flex-col gap-2 border-b border-border px-4 py-3 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <div className="text-sm font-medium text-ink">
              What are you all using for cross-team project tracking in 2026?
            </div>
            <div className="mt-0.5 text-[12px] text-muted-foreground">
              r/ProductManagement • Commented 18 days ago
            </div>
          </div>
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-brand-accent px-2.5 py-1 text-[11px] font-semibold text-white">
            <Sparkles className="h-3 w-3" />
            Cited by AI, Perplexity
          </span>
        </div>

        <div className="flex flex-col gap-2 border-b border-border px-4 py-3 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <div className="text-sm font-medium text-ink">
              Monday.com vs Asana for a 60-person ops team, worth the price jump?
            </div>
            <div className="mt-0.5 text-[12px] text-muted-foreground">r/sysadmin • Commented 9 days ago</div>
          </div>
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-signal-teal/40 bg-signal-teal/10 px-2.5 py-1 text-[11px] font-semibold text-signal-teal-ink">
            <CheckCircle2 className="h-3 w-3" />
            In thread
          </span>
        </div>

        <div className="flex flex-col gap-2 px-4 py-3 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <div className="text-sm font-medium text-ink">Anyone actually happy with their PM tool after 12 months?</div>
            <div className="mt-0.5 text-[12px] text-muted-foreground">r/smallbusiness • Commented 3 days ago</div>
          </div>
          <span className="inline-flex shrink-0 items-center rounded-full border border-border px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
            Awaiting, 3 days
          </span>
        </div>
      </div>
    </div>
  );
}

/** Compact teaser used on the homepage. */
export function RedditGapsDemoTeaser() {
  return (
    <div className="force-light rounded-2xl border border-border bg-paper p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <span className="text-[13px] text-brand-accent-ink font-medium">Reddit Gaps</span>
        <DemoBadge />
      </div>
      <div className="rounded-xl border border-border bg-card">
        {DEMO_THREADS.slice(0, 2).map((t) => (
          <div key={t.id} className="border-b border-border p-3 last:border-0">
            <div className="text-sm font-semibold leading-snug text-ink">{t.title}</div>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
              <span>r/{t.subreddit}</span>
              <span className="inline-flex items-center gap-1">
                <ArrowUp className="h-3 w-3" />
                {t.upvotes}
              </span>
              <span className="inline-flex items-center gap-1">
                <MessageSquare className="h-3 w-3" />
                {t.comments}
              </span>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {t.competitors.map((c) => (
                <span key={c} className="rounded-md border border-border bg-panel px-1.5 py-0.5 text-[11px] font-medium text-ink">
                  {c}
                </span>
              ))}
              <span className="rounded-md bg-signal-coral/10 px-1.5 py-0.5 text-[11px] font-medium text-signal-coral-ink">
                Not you
              </span>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-1.5 text-[12px] font-medium text-brand-accent-ink">
        <Sparkles className="h-3.5 w-3.5" />
        Track the ones you reply to, and see when AI cites them.
      </div>
    </div>
  );
}
